import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { http } from "@/lib/api";
import PanicIndexCard from "@/components/predictive/PanicIndexCard";
import ContributingFactors from "@/components/predictive/ContributingFactors";
import ForecastHeatmap from "@/components/predictive/ForecastHeatmap";
import ForecastChart from "@/components/predictive/ForecastChart";
import ZoneForecastTable from "@/components/predictive/ZoneForecastTable";
import WhatIfPanel from "@/components/predictive/WhatIfPanel";

const HORIZONS = [15, 30, 60];
const REFRESH_MS = 8000;

export default function Predictive() {
  const { bootstrap } = useOutletContext();
  const [horizon, setHorizon] = useState(30);
  const [panic, setPanic] = useState(null);
  const [forecasts, setForecasts] = useState(null);
  const [focusZone, setFocusZone] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [p, f] = await Promise.all([
        http.get("/predictive/panic-index").then((r) => r.data),
        http.get(`/predictive/forecasts?horizons=${HORIZONS.join(",")}`).then((r) => r.data),
      ]);
      setPanic(p);
      setForecasts(f);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // Default focus = highest-risk zone at the selected horizon
  const topZone = useMemo(() => {
    if (!forecasts?.zones) return null;
    const sorted = [...forecasts.zones].sort((a, b) => {
      const A = a.forecasts?.find((f) => f.horizon_min === horizon)?.pct ?? 0;
      const B = b.forecasts?.find((f) => f.horizon_min === horizon)?.pct ?? 0;
      return B - A;
    });
    return sorted[0] || null;
  }, [forecasts, horizon]);

  const activeFocus = focusZone || (topZone ? { id: topZone.zone_id, name: topZone.zone_name } : null);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Slim title row + horizon selector */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0 gap-2 flex-wrap">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">
            Predictive Risk
          </h1>
          <span className="micro-label hidden sm:inline">Forecasts · Panic Index · What-If</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="micro-label">Horizon</span>
          {HORIZONS.map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              data-testid={`horizon-${h}`}
              className={[
                "px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                horizon === h
                  ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                  : "border-border text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              T+{h}m
            </button>
          ))}
          <button
            onClick={fetchAll}
            className="btn-tac !px-2 !py-1.5"
            data-testid="predictive-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {/* Row 1: Panic index + factors */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <PanicIndexCard data={panic} />
          <div className="lg:col-span-2">
            <ContributingFactors factors={panic?.factors ?? []} />
          </div>
        </div>

        {/* Row 2: forecast heatmap + chart for selected zone */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-2">
          <div className="panel xl:col-span-3 min-h-[400px] flex flex-col">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
              <span className="h-widget">Forecast Heatmap</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {forecasts?.zones?.length ?? 0} zones · T+{horizon}m
              </span>
            </div>
            <div className="flex-1 grid-bg p-2 min-h-0">
              <ForecastHeatmap
                forecasts={forecasts?.zones ?? []}
                horizonMin={horizon}
                onZoneClick={(zid) => {
                  const z = forecasts?.zones?.find((x) => x.zone_id === zid);
                  if (z) setFocusZone({ id: z.zone_id, name: z.zone_name });
                }}
              />
            </div>
          </div>

          <div className="panel xl:col-span-2 min-h-[400px]">
            <ForecastChart
              zoneId={activeFocus?.id}
              zoneName={activeFocus?.name}
              horizonMin={60}
            />
          </div>
        </div>

        {/* Row 3: per-zone projection table */}
        <ZoneForecastTable
          zones={forecasts?.zones ?? []}
          onZoneClick={(id, name) => setFocusZone({ id, name })}
        />

        {/* Row 4: what-if simulator */}
        <WhatIfPanel zones={bootstrap?.zones ?? []} />
      </div>
    </div>
  );
}
